/**
 * Custom element for the Content Cards section.
 *
 * Behaviour:
 *  - 1–4 cards  → static flex row, no arrows rendered
 *  - 5+ cards   → horizontal scroll-snap slider; arrows advance 1 card at a time
 */
class ContentCards extends HTMLElement {
  /** @type {HTMLElement} */
  #track;
  /** @type {HTMLButtonElement|null} */
  #prevBtn;
  /** @type {HTMLButtonElement|null} */
  #nextBtn;

  connectedCallback() {
    this.#track = this.querySelector('.ncc__track');
    this.#prevBtn = this.querySelector('.ncc__arrow--prev');
    this.#nextBtn = this.querySelector('.ncc__arrow--next');

    if (!this.#track) return;

    if (this.#prevBtn) {
      this.#prevBtn.addEventListener('click', () => this.#scroll(-1));
    }
    if (this.#nextBtn) {
      this.#nextBtn.addEventListener('click', () => this.#scroll(1));
    }

    this.#track.addEventListener('scroll', () => this.#updateArrows(), { passive: true });

    // Update once layout is stable
    requestAnimationFrame(() => this.#updateArrows());

    // Re-evaluate on resize (viewport or font size change)
    this.#resizeObserver = new ResizeObserver(() => this.#updateArrows());
    this.#resizeObserver.observe(this.#track);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver|undefined} */
  #resizeObserver;

  /**
   * Scrolls the track by one card width in the given direction.
   * @param {-1|1} dir
   */
  #scroll(dir) {
    const card = this.#track.querySelector('.ncc__card');
    if (!card) return;
    const cardWidth = card.getBoundingClientRect().width + this.#getGap();
    this.#track.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
  }

  /** Returns the computed column-gap of the track. */
  #getGap() {
    return parseFloat(getComputedStyle(this.#track).columnGap) || 0;
  }

  /** Enables / disables the arrow buttons based on current scroll position. */
  #updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;

    // A scrollable region needs a keyboard way to scroll, but only while it overflows and has no focusable content
    if (scrollWidth > clientWidth + 1 && !this.#track.querySelector('a[href], button')) {
      this.#track.setAttribute('tabindex', '0');
      this.#track.setAttribute('aria-label', this.dataset.trackLabel || '');
      // A list keeps its own role; a plain div needs one for the label to be announced
      if (!this.#track.hasAttribute('role')) this.#track.setAttribute('role', 'group');
    } else {
      this.#track.removeAttribute('tabindex');
      this.#track.removeAttribute('aria-label');
      if (this.#track.getAttribute('role') === 'group') this.#track.removeAttribute('role');
    }

    if (!this.#prevBtn || !this.#nextBtn) return;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    this.#prevBtn.disabled = atStart;
    this.#nextBtn.disabled = atEnd;

    this.#prevBtn.setAttribute('aria-disabled', String(atStart));
    this.#nextBtn.setAttribute('aria-disabled', String(atEnd));
  }
}

customElements.define('content-cards', ContentCards);
